"use client";
import { usePowerSearch } from "./contexts/power-search-context";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Card from "react-bootstrap/Card";
import Button from "react-bootstrap/Button";
import Image from "react-graceful-image";
import ReactPaginate from "react-paginate";
import logo from "images/logo-full/open-director-full-logo.png";
import { SkeletonGridCard, SkeletonListCard } from "./LoadingSkeleton";

import ResultCardGridItem from "./SearchResults/ResultCardGrid";
import ResultListItem from "./SearchResults/ResultCardListItem";
import SearchResultsHeader from "./SearchResults/SearchResultsHeader";
import { getAllEntityMembers } from "./utils/searchUtils";
import { useTab } from "./contexts/TabContext";

const GridResultContainer = ({ children }) => (
  <Row className="g-3 grid-view-active" style={{ margin: 0 }}>
    {children}
  </Row>
);

const ListResultContainer = ({ children }) => (
  <Row className="g-3 list-view-active" style={{ margin: 0 }}>
    {children}
  </Row>
);

const GridResult = ({ searchResults }) => (
  <GridResultContainer>
    {searchResults.map((result, index) => {
      const allMemberships = getAllEntityMembers(result);
      return (
        <Col key={result.id || index} className="col-lg-4 mb-4">
          <ResultCardGridItem result={result} allMemberships={allMemberships} />
        </Col>
      );
    })}
  </GridResultContainer>
);

const ListResult = ({ searchResults }) => (
  <ListResultContainer>
    {searchResults.map((result, index) => {
      const allMemberships = getAllEntityMembers(result);
      return (
        <Col xs={12} key={result.id || index}>
          <ResultListItem result={result} allMemberships={allMemberships} />
        </Col>
      );
    })}
  </ListResultContainer>
);

const GridSkeleton = () => (
  <GridResultContainer>
    <Col className="col-lg-4 mb-4">
      <SkeletonGridCard />
      <SkeletonGridCard />
      <SkeletonGridCard />
    </Col>
    <Col className="col-lg-4 mb-4">
      <SkeletonGridCard />
      <SkeletonGridCard />
      <SkeletonGridCard />
    </Col>
    <Col className="col-lg-4 mb-4">
      <SkeletonGridCard />
      <SkeletonGridCard />
      <SkeletonGridCard />
    </Col>
  </GridResultContainer>
);

const ListSkeleton = () => {
  return (
    <ListResultContainer>
      <Col xs={12}>
        <SkeletonListCard />
      </Col>
      <Col xs={12}>
        <SkeletonListCard />
      </Col>
      <Col xs={12}>
        <SkeletonListCard />
      </Col>
    </ListResultContainer>
  );
};

const SearchResultsContainer = ({ children }) => {
  return (
    <Card
      className="d-flex p-3 w-100 flex-column align-items-center justify-content-center text-center"
      style={{
        height: "70%",
        padding: "100px",
        borderTopLeftRadius: "0",
        borderBottomLeftRadius: "0",
      }}
    >
      {children}
    </Card>
  );
};

const SearchResults = ({ currentView }) => {
  const { activeTab } = useTab();
  const {
    searchResults,
    searchResultsLoading,
    showResults,
    totalPages,
    currentPage,
    handlePageChange,
  } = usePowerSearch();

  if (!showResults && !searchResultsLoading) {
    return (
      <SearchResultsContainer>
        <Image src={logo} alt="Company Logo" className="my-5 img-fluid" />
        <h1 style={{ color: "#001F38" }}>
          Start your search by customising your search criteria
        </h1>
      </SearchResultsContainer>
    );
  }

  if (searchResults.length === 0 && showResults && !searchResultsLoading) {
    return (
      <Card
        className="d-flex p-3 w-100 flex-column align-items-center justify-content-center text-center"
        style={{
          height: "40%",
          padding: "100px",
          borderTopLeftRadius: "0",
          borderBottomLeftRadius: "0",
        }}
      >
        {" "}
        <p className="text-center w-100 p-5">No results found.</p>
      </Card>
    );
  }

  const showControls = showResults && searchResults.length > 0;

  return (
    <Card
      style={{
        paddingLeft: "8px",
        paddingRight: "8px",
        borderTopLeftRadius: 0,
        borderBottomLeftRadius: 0,
        position: "relative",
      }}
    >
      <SearchResultsHeader />
      {currentView === "grid" && !searchResultsLoading && (
        <GridResult searchResults={searchResults} />
      )}
      {currentView === "list" && !searchResultsLoading && (
        <ListResult searchResults={searchResults} />
      )}

      {searchResultsLoading && currentView === "grid" && <GridSkeleton />}
      {searchResultsLoading && currentView === "list" && <ListSkeleton />}

      {showControls && totalPages > 1 && activeTab === "search" && (
        <Row className="d-flex justify-content-center w-100 mt-4">
          <div
            style={{
              position: "relative",
            }}
          >
            <ReactPaginate
              disabled={searchResultsLoading}
              previousLabel={"Previous"}
              nextLabel={"Next"}
              breakLabel={"..."}
              pageCount={totalPages}
              forcePage={currentPage - 1}
              marginPagesDisplayed={2}
              pageRangeDisplayed={3}
              onPageChange={(data) => handlePageChange(data.selected + 1)}
              pageClassName={"page-item"}
              pageLinkClassName={"page-link"}
              activeClassName={"active"}
              previousClassName={"page-item"}
              nextClassName={"page-item"}
              previousLinkClassName={"page-link"}
              nextLinkClassName={"page-link"}
              breakClassName={"page-item"}
              breakLinkClassName={"page-link"}
              containerClassName="powersearch-pagination pagination"
            />
          </div>
        </Row>
      )}
    </Card>
  );
};

export default SearchResults;
